// FeedbackButton — 「タカミン窓口」フィードバックボタン + モーダル (= 超 MVP, 2026-05-23 投入).
//
// 背景・戦略: launch-rollout-plan.md §3.3 / §3.3.7 + §3.3.1 強化 (2026-05-22 前倒し).
// - 画面右下にタカミンアイコン (`takamin_thankyou.png`, 36px) をフローティング配置.
// - 押下でモーダルを開く: 自由記述テキストエリア + 注釈 + 送信ボタン.
// - 送信は WS `feedback.submit` (protocol/events/feedback.ts §3.3.7).
//   sender 単独 ack / rejected を listener で待つ.
//
// 2026-05-22 強化 (= W2 投入前に前倒し、launch-rollout-plan.md §3.3.1):
//   - 吹き出し演出 (パターン A/B/C、頻度 30s → 2min → 5-10min 抑制ロジック)
//   - 出現/切替/消失時 + ドラッグ中の「ぴくぴく」(= 200ms 切替アニメ拡張)
//   - ドラッグ移動 + localStorage 位置保存 + ダブルクリックでデフォルト復帰
//   - 描画中抑制 (= stroke.* event を観測して直近 5s 内発火なら吹き出し抑制)
//   - モーダル既開時は吹き出し停止 (= 「停止」採用、agent prompt 制約 5)
//   - prefers-reduced-motion: reduce 設定時はぴくぴく全停止、吹き出しテキスト切替のみ
//
// MVP 仕様 (= frontend-developer agent prompt の指示通り):
//   1. アニメーション (200ms 切替 + `takamin_thankyou2.png`) は実装しない (後追い).
//   2. ラベルは付けない (= アイコンそのものが入口).
//   3. accessibility: `aria-label` で「タカミンにメッセージを送る」を提示.
//   4. コンテキスト自動添付 (UA / undo log / viewport) は実装しない (後追い).
//      `message` のみ送信. `roomId` は **現状ルームに居る場合のみ optional 添付**.
//
// UX フロー:
//   - 入力 → 送信ボタン押下:
//     a) client-side validate (`validateFeedbackMessage`). NG なら error 表示のみ.
//     b) `feedback.submit` を送出. ボタン disable + 「送信中…」スピナー表示.
//     c) `feedback.ack` 受信 → 「送信しました、ありがとうございます」+ 自動 close.
//     d) `feedback.rejected` 受信 → reason に応じた日本語文言で error 表示, 再送可.
//
// 設計 (= 既存 modal 流儀踏襲):
//   - ConfirmDialog と同じく **outer (FeedbackButton) + view (FeedbackModalView)** に
//     分離して、view を pure 関数として node-env テストできるようにする.
//   - SessionProvider の `transport` を直接 listener subscribe + send で使う.
//     (= 既存の `chat.send` / `whiteboard.clear` と同じ流儀、useCases.ts への
//      thin helper 追加もできるが、ack/rejected を待つ stateful 経路なので
//      コンポーネント内で完結させる方が読みやすい.)
//   - 「現状ルームに居る場合」は `useSession().view.phase.t === 'joined'` で判定し、
//      `joined` 時に限り `roomId` を添付.
//   - 送信中の wait は **タイムアウトを設けない**. WS 切断時は transport の close
//     event 経由で UI を「送信失敗」表示に倒すこともできるが、超 MVP では割愛
//     (= 切断 → ack も rejected も来ない → ボタン disable のまま modal 残る) →
//     ユーザはモーダルを ✕ で閉じて再 open + 再送できる. (W2 期間中の改善対象.)
//
// z-index: 既存 modal `z-50` の上 (= ConfirmDialog と同 `z-[60]`) に出す. 同時に
// 出ても fixed 配置の浮きボタンより上にいて良い.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  type BubblePattern,
  clampPosition,
  createFeedbackButtonPositionStore,
  type FeedbackButtonPosition,
  isDragMovement,
  nextBubbleDelayMs,
  pickRandomBubblePattern,
} from '../application/feedbackBubble.js';
import {
  FEEDBACK_MESSAGE_MAX_LENGTH,
  mapFeedbackRejectedReason,
  validateFeedbackMessage,
} from '../application/feedbackForm.js';
// 4 アイコンを import 経由で参照することで Vite ビルド時に content hash 付き
// ファイル名 (= [name]-[hash].png) で出力させ、prod での自動 cache busting を
// 効かせる. `public/` 経由だとファイル名固定で配信されてしまい、画像差し替え時
// にユーザのブラウザ / CDN に古いキャッシュが残る問題が出る.
import iconFb1 from '../assets/feedback-icons/takamin_fb-1.png';
import iconFb2 from '../assets/feedback-icons/takamin_fb-2.png';
import iconFbDrag1 from '../assets/feedback-icons/takamin_fb-drag-1.png';
import iconFbDrag2 from '../assets/feedback-icons/takamin_fb-drag-2.png';
import { useSession } from './hooks/useSession.js';

// 描画中抑制の窓 (= stroke 系 event を最後に観測した時刻からこの ms 内なら描画中
// 扱いで吹き出し発動を抑制). launch-rollout-plan.md §3.3.1 仕様の「描画中抑制」.
// pointerup を sniffer する代わりに、stroke.* event の流れを heuristic として使う
// (= agent prompt 制約 4 推奨経路、見えない描画 state を侵さずに済む).
const DRAWING_QUIET_WINDOW_MS = 5_000;

// ボタンサイズ (= h-9 w-9 ≒ 36px). clamp 計算で使う.
const BUTTON_SIZE_PX = 36;

/**
 * 送信ライフサイクル. modal 内の view state. error/success のメッセージは
 * sub-state として一緒に持つ (Reducer 化はオーバーキル).
 */
type SubmitState =
  | { readonly t: 'idle' }
  | { readonly t: 'validating'; readonly error: string }
  | { readonly t: 'submitting' }
  | { readonly t: 'success' }
  | { readonly t: 'error'; readonly message: string };

export interface FeedbackModalViewProps {
  readonly open: boolean;
  readonly message: string;
  readonly onMessageChange: (next: string) => void;
  readonly submitState: SubmitState;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
  readonly titleId: string;
  readonly messageInputId: string;
  readonly errorTextId: string;
  /** textarea autofocus 用 ref. open 時 effect で focus する. */
  readonly textareaRef?: React.Ref<HTMLTextAreaElement>;
}

/**
 * Pure view (props ですべて決まる). React tree-walking 単体テスト対応.
 *
 * leak しがちな a11y / focus 管理 (Esc / autoFocus) と modal 開閉 lifecycle
 * は外側 `FeedbackButton` で処理し、ここは入力 + ボタンの DOM 構造に集中する.
 */
export const FeedbackModalView = ({
  open,
  message,
  onMessageChange,
  submitState,
  onSubmit,
  onCancel,
  titleId,
  messageInputId,
  errorTextId,
  textareaRef,
}: FeedbackModalViewProps): React.ReactNode => {
  if (!open) return null;
  const isSubmitting = submitState.t === 'submitting';
  const isSuccess = submitState.t === 'success';
  // 送信ボタン enable 判定: success 表示中 (= 自動 close 待ち) や送信中は disable.
  // input 空 (= trim 後) も disable. validate error は表示するが送信は止める.
  const disabled = isSubmitting || isSuccess || message.trim().length === 0;
  const errorText =
    submitState.t === 'error'
      ? submitState.message
      : submitState.t === 'validating'
        ? submitState.error
        : null;
  // 文字数カウンタ. counter は trim 前の生長さで表示 (= 入力欄に見えている量と
  // 一致). 上限超過時は赤系で警告. cap が近づいたら user が気付ける UX.
  const rawLength = message.length;
  const overLimit = rawLength > FEEDBACK_MESSAGE_MAX_LENGTH;

  return (
    // ConfirmDialog の z-[60] と同様. 既存 modal (z-50) より上に乗せる.
    <div className="fixed inset-0 z-[60] grid place-items-center p-4">
      {/* Backdrop. backdrop click で onCancel (= submitting 中は無効化). */}
      <button
        type="button"
        aria-label="閉じる"
        tabIndex={-1}
        onClick={isSubmitting ? undefined : onCancel}
        className="-z-10 absolute inset-0 cursor-default bg-black/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // w-full max-w + min-w-0: max-w-full だけでは grid 子の intrinsic min が
        // max-w を貫通し得る (2026-07-03 一括点検、AnnounceEntryModal と同パターン)。
        className="max-h-[90vh] w-full min-w-0 max-w-[480px] overflow-y-auto rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
      >
        <h2 id={titleId} className="mb-2 text-base font-bold text-[var(--color-accent)]">
          お声をお寄せください
        </h2>
        {/* タイトル直下に「気軽に何でも」のトーン説明 (= タカミン氏一人称、運営者と
            して直接語りかける形). 既存注釈は仕組みの説明なので三人称維持で対比
            (= 説明文 = タカミンの「声」、注釈 = 「仕組み」). */}
        <p className="mb-3 text-xs text-[var(--color-text-muted)] leading-relaxed">
          バグや要望、ご感想、昔の思い出話、私へのひとことなど、気軽に何でも聞かせてください。今後の開発の励みになります。
        </p>
        <label htmlFor={messageInputId} className="sr-only">
          フィードバックメッセージ
        </label>
        <textarea
          id={messageInputId}
          ref={textareaRef}
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          disabled={isSubmitting || isSuccess}
          rows={8}
          maxLength={FEEDBACK_MESSAGE_MAX_LENGTH + 100 /* 入力体験を阻害しない buffer */}
          aria-describedby={errorText !== null ? errorTextId : undefined}
          className="mb-2 w-full resize-y rounded border border-[var(--color-border)] bg-[var(--color-bg)] p-2 text-sm text-[var(--color-text)] focus:border-[var(--color-accent)] focus:outline-none disabled:opacity-60"
          placeholder="ご感想・バグ報告・要望、なんでもどうぞ"
        />
        {/* 文字数カウンタ + 上限超過警告. raw length を見せる方が「貼り付けた長文が
            どれだけオーバーしているか」が分かる UX. */}
        <p
          className={`mb-3 text-right text-xs ${
            overLimit ? 'text-[var(--color-error)]' : 'text-[var(--color-text-muted)]'
          }`}
        >
          {rawLength} / {FEEDBACK_MESSAGE_MAX_LENGTH}
        </p>
        {/* 注釈 (= launch-rollout-plan.md §3.3.7 文言). */}
        <p className="mb-1 text-xs text-[var(--color-text-muted)]">
          バグ報告の際は、お使いのブラウザや環境、再現手順も書いていただけると助かります
        </p>
        {/* エラー / 成功表示. aria-describedby で textarea に紐づく. */}
        {errorText !== null && (
          <p id={errorTextId} role="alert" className="mb-2 text-xs text-[var(--color-error)]">
            {errorText}
          </p>
        )}
        {isSuccess && (
          <p role="status" className="mb-2 text-xs text-[var(--color-accent)]">
            送信しました、ありがとうございます
          </p>
        )}
        <div className="mb-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-4 py-2 text-sm text-white hover:bg-[var(--color-bg)] disabled:opacity-50"
          >
            閉じる
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={disabled}
            className="rounded bg-[var(--color-accent)] px-4 py-2 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? '送信中…' : '送信'}
          </button>
        </div>
        {/* 返信ポリシー. 期待値管理の核. */}
        <div className="border-t border-white/10 pt-3 text-xs text-[var(--color-text-muted)]">
          <p className="mb-1">※ 個別のお返事はできない仕組みです、ごめんなさい</p>
          <p className="mb-1 pl-3">
            返信が必要な内容は X (
            <a
              href="https://x.com/takamin123"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-accent)] underline hover:no-underline"
            >
              @takamin123
            </a>
            ) までお願いします
          </p>
          <p>※ いただいた内容は今後の開発の参考にさせていただきます</p>
        </div>
      </div>
    </div>
  );
};

export interface FeedbackButtonProps {
  /**
   * テスト用に open 状態を controlled で渡す経路. 通常は省略 (= 内部 state).
   */
  readonly initialOpen?: boolean;
}

/**
 * 画面右下フローティング配置のタカミンアイコンボタン. 押下でフィードバック
 * モーダルを開く.
 *
 * `useSession()` を使うので必ず `<SessionProvider>` の内側で呼ぶこと.
 * (= MVP では `ClassicLayout` 内に置く. HomePage / LoginPage 等への展開は
 *  WS 未接続でも feedback を送れる経路が必要になるため後追い.)
 */
export const FeedbackButton = ({ initialOpen = false }: FeedbackButtonProps): React.ReactNode => {
  const { transport, view } = useSession();
  const [open, setOpen] = useState<boolean>(initialOpen);
  const [message, setMessage] = useState<string>('');
  const [submitState, setSubmitState] = useState<SubmitState>({ t: 'idle' });
  // 万歳アニメ用 state. ランダム間隔 (10-30s) で 200ms 切り替える (§3.3.1).
  // メンバーリスト等の白背景に溶けないよう「気づかれる」要素として動かす.
  // 加えて吹き出しの出現/切替/消失時 + ドラッグ中も同 state を切り替えて
  // 「ぴくぴく」を実現する.
  const [waving, setWaving] = useState<boolean>(false);
  // 吹き出し state. null = 非表示、object = 現在表示中の文言.
  const [bubbleText, setBubbleText] = useState<string | null>(null);
  // 現在の位置. null = デフォルト (= 右下 CSS 固定). drag で px 値に切替.
  const [position, setPosition] = useState<FeedbackButtonPosition | null>(null);
  // ドラッグ中フラグ (= 「持続ぴくぴく」 + click 抑制 + cursor 変更用).
  const [dragging, setDragging] = useState<boolean>(false);
  const titleId = useId();
  const messageInputId = useId();
  const errorTextId = useId();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // success 後の自動 close 用 timer. cleanup できるよう ref で保持.
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 位置 store. lazy 1 回生成. (モーダル既開 store は 2026-05-22 戦略変更で廃止、
  // 吹き出しは永遠に出続ける + ×ボタン後追い narrative arc に移行.)
  const positionStoreRef = useRef(createFeedbackButtonPositionStore());
  // 最後に観測した stroke.* event の時刻 (= 描画中抑制 heuristic).
  const lastStrokeAtRef = useRef<number>(0);
  // 次の吹き出し発火予定時刻 (epoch ms). debug 表示用 (= import.meta.env.DEV 限定).
  // scheduler 内で setTimeout 仕込むたびに更新、別 useEffect で 250ms ごとに
  // countdown state へ反映する.
  const nextFireAtRef = useRef<number | null>(null);
  const [debugCountdown, setDebugCountdown] = useState<number | null>(null);

  // 現状ルームに居るときだけ roomId を payload に添付 (= MVP 仕様、§3.3.7 後追い
  // 項目では UA / undo log 等を更に積む).
  const currentRoomId =
    view.phase.t === 'joined' || view.phase.t === 'reconnecting' ? view.phase.state.roomId : null;

  // modal を閉じるとき: state を全部 reset. ただし submitting 中の中断は許さない
  // (= ack/rejected を待ってる間に閉じると後から rejected が到来して残った state
  //  に書き込まれて UX が混乱する → submitting 中は backdrop / cancel ボタンが
  //  そもそも disable, ✕ も無し).
  const closeModal = useCallback((): void => {
    if (submitState.t === 'submitting') return;
    setOpen(false);
    setMessage('');
    setSubmitState({ t: 'idle' });
    if (successTimerRef.current !== null) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
  }, [submitState.t]);

  // Esc で閉じる (submitting 中は無視). ConfirmDialog と同パターン.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [open, closeModal]);

  // open 直後に textarea に focus を当てる (= ユーザは押下直後に文字を打ち始められる).
  useEffect(() => {
    if (!open) return;
    textareaRef.current?.focus();
  }, [open]);

  // unmount cleanup. success timer の残りを clear.
  useEffect(() => {
    return () => {
      if (successTimerRef.current !== null) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    };
  }, []);

  // 万歳アニメ. ランダム間隔 (10-30 秒) で 200ms だけ takamin_thankyou2.png に
  // 切り替え、視認性を上げる (§3.3.1). prefers-reduced-motion: reduce 設定の
  // ユーザには完全に発火させない (= 動きが苦手なユーザ配慮).
  // ドラッグ中はこの timer は走らせず、別 useEffect で「持続ぴくぴく」を担当.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (dragging) return;
    const prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduce) return;

    let waveTimer: ReturnType<typeof setTimeout> | null = null;
    let returnTimer: ReturnType<typeof setTimeout> | null = null;

    const schedule = (): void => {
      const delay = 10_000 + Math.random() * 20_000;
      waveTimer = setTimeout(() => {
        setWaving(true);
        returnTimer = setTimeout(() => {
          setWaving(false);
          schedule();
        }, 200);
      }, delay);
    };
    schedule();

    return () => {
      if (waveTimer !== null) clearTimeout(waveTimer);
      if (returnTimer !== null) clearTimeout(returnTimer);
    };
  }, [dragging]);

  // 描画中抑制 (= heuristic). stroke.start / stroke.segment / stroke.end を観測し、
  // 最後の発火時刻を ref に保存. 吹き出し scheduler はこの時刻を参照して
  // 「直近 DRAWING_QUIET_WINDOW_MS 内に描画があれば」発動を skip する.
  // 自分の描画だけでなく他人の描画でも発火するが、他人の描画タイミングで
  // 吹き出しが邪魔になるケースも軽減できるので結果的にちょうど良い.
  useEffect(() => {
    const off = transport.onEvent((event) => {
      if (event.t === 'stroke.start' || event.t === 'stroke.segment' || event.t === 'stroke.end') {
        lastStrokeAtRef.current = Date.now();
      }
    });
    return off;
  }, [transport]);

  // 位置の初期読み込み (= 保存されていればクランプして使う、なければデフォルト = null).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = positionStoreRef.current.read();
    if (saved === null) return;
    const clamped = clampPosition({
      x: saved.x,
      y: saved.y,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      buttonSize: BUTTON_SIZE_PX,
    });
    setPosition(clamped);
    // クランプ結果がストア値と違ったら再保存 (= 次回も viewport 内に収める).
    if (clamped.x !== saved.x || clamped.y !== saved.y) {
      positionStoreRef.current.write(clamped);
    }
  }, []);

  // 画面リサイズ時の再クランプ.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = (): void => {
      setPosition((cur) => {
        if (cur === null) return null;
        const clamped = clampPosition({
          x: cur.x,
          y: cur.y,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          buttonSize: BUTTON_SIZE_PX,
        });
        // 値が変わったときだけ store も更新.
        if (clamped.x !== cur.x || clamped.y !== cur.y) {
          positionStoreRef.current.write(clamped);
        }
        return clamped;
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // ドラッグ中の「持続ぴくぴく」(= 200ms 間隔で連続切替). reduced-motion なら停止.
  useEffect(() => {
    if (!dragging) return;
    if (typeof window === 'undefined') return;
    const prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduce) return;
    const id = setInterval(() => {
      setWaving((w) => !w);
    }, 200);
    return () => {
      clearInterval(id);
      setWaving(false);
    };
  }, [dragging]);

  // 吹き出し scheduler. 入室直後 (= mount) から 30s 後に初回、その後 2 分後、
  // 以降 5-10 分ランダム. 描画中 / モーダル既開 / モーダル open 中は skip.
  // パターン選択は連続同パターン回避 (= pickRandomBubblePattern).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let firedCount = 0;
    let lastPatternId: string | null = null;
    let scheduleTimer: ReturnType<typeof setTimeout> | null = null;
    let segmentTimers: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    const clearSegmentTimers = (): void => {
      for (const t of segmentTimers) clearTimeout(t);
      segmentTimers = [];
    };

    const playPattern = (pattern: BubblePattern): number => {
      // 各 segment を順に表示. setTimeout チェーンで切替.
      let cumulative = 0;
      for (const seg of pattern.segments) {
        const showAt = cumulative;
        const t = setTimeout(() => {
          if (cancelled) return;
          setBubbleText(seg.text);
          // 切替時にアイコンぴくぴく (= 200ms × 2 回素早く揺れる).
          // reduced-motion なら text 切替のみ.
          if (!prefersReduce) {
            setWaving(true);
            const a = setTimeout(() => {
              if (cancelled) return;
              setWaving(false);
              const b = setTimeout(() => {
                if (cancelled) return;
                setWaving(true);
                const c = setTimeout(() => {
                  if (cancelled) return;
                  setWaving(false);
                }, 100);
                segmentTimers.push(c);
              }, 100);
              segmentTimers.push(b);
            }, 100);
            segmentTimers.push(a);
          }
        }, showAt);
        segmentTimers.push(t);
        cumulative += seg.durationMs;
      }
      // 最後に吹き出し非表示 + ぴくぴく (= 消失時).
      const hideT = setTimeout(() => {
        if (cancelled) return;
        setBubbleText(null);
        if (!prefersReduce) {
          setWaving(true);
          const r = setTimeout(() => {
            if (cancelled) return;
            setWaving(false);
          }, 200);
          segmentTimers.push(r);
        }
      }, cumulative);
      segmentTimers.push(hideT);
      return cumulative;
    };

    const tick = (): void => {
      if (cancelled) return;
      // 描画中なら 1 秒後リトライ (= 待ってまた判定).
      const now = Date.now();
      if (now - lastStrokeAtRef.current < DRAWING_QUIET_WINDOW_MS) {
        nextFireAtRef.current = now + 1_000;
        scheduleTimer = setTimeout(tick, 1_000);
        return;
      }
      const pattern = pickRandomBubblePattern(lastPatternId);
      lastPatternId = pattern.id;
      const totalMs = playPattern(pattern);
      firedCount += 1;
      const nextDelay = nextBubbleDelayMs(firedCount);
      nextFireAtRef.current = now + totalMs + nextDelay;
      scheduleTimer = setTimeout(tick, totalMs + nextDelay);
    };

    // 初回 schedule (= 30 秒後).
    nextFireAtRef.current = Date.now() + nextBubbleDelayMs(0);
    scheduleTimer = setTimeout(tick, nextBubbleDelayMs(0));

    return () => {
      cancelled = true;
      if (scheduleTimer !== null) clearTimeout(scheduleTimer);
      clearSegmentTimers();
      setBubbleText(null);
      nextFireAtRef.current = null;
    };
  }, []);

  // Debug: 次の発火までのカウントダウンを 250ms ごとに state へ反映.
  // import.meta.env.DEV 限定で UI に出すので、prod ビルドではこの effect ごと
  // 不要 (= 早期 return で setInterval も走らない).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const update = (): void => {
      const fireAt = nextFireAtRef.current;
      if (fireAt === null) {
        setDebugCountdown(null);
        return;
      }
      setDebugCountdown(Math.max(0, Math.ceil((fireAt - Date.now()) / 1000)));
    };
    update();
    const id = setInterval(update, 250);
    return () => clearInterval(id);
  }, []);

  // ドラッグハンドラ (= pointer events: mouse + touch 両対応).
  // pointer down 位置と down 時のボタン位置を保持し、move で差分計算 + clamp.
  // up 時に「移動量 < 閾値」ならクリック扱いで modal を開く.
  const dragStartRef = useRef<{
    readonly pointerId: number;
    readonly downX: number;
    readonly downY: number;
    readonly originX: number;
    readonly originY: number;
    moved: boolean;
  } | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>): void => {
    if (typeof window === 'undefined') return;
    // 右クリック / 中クリックは無視.
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const rect = e.currentTarget.getBoundingClientRect();
    dragStartRef.current = {
      pointerId: e.pointerId,
      downX: e.clientX,
      downY: e.clientY,
      originX: rect.left,
      originY: rect.top,
      moved: false,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // 一部 browser で例外、無視.
    }
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>): void => {
    if (typeof window === 'undefined') return;
    const start = dragStartRef.current;
    if (start === null || start.pointerId !== e.pointerId) return;
    const dx = e.clientX - start.downX;
    const dy = e.clientY - start.downY;
    if (!start.moved && !isDragMovement(dx, dy)) return;
    if (!start.moved) {
      start.moved = true;
      setDragging(true);
    }
    const next = clampPosition({
      x: start.originX + dx,
      y: start.originY + dy,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      buttonSize: BUTTON_SIZE_PX,
    });
    setPosition(next);
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLButtonElement>): void => {
    const start = dragStartRef.current;
    if (start === null || start.pointerId !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore.
    }
    const moved = start.moved;
    dragStartRef.current = null;
    if (moved) {
      setDragging(false);
      // 移動結果を store に保存.
      setPosition((cur) => {
        if (cur !== null) positionStoreRef.current.write(cur);
        return cur;
      });
    } else {
      // クリック扱い: modal を開く. (既開フラグ立て上げは 2026-05-22 戦略変更で廃止.)
      setOpen(true);
    }
  }, []);

  // ダブルクリックでデフォルト位置 (= 右下) に戻す. position を null に倒すと
  // CSS の right-4 bottom-4 が効く.
  const handleDoubleClick = useCallback((): void => {
    setPosition(null);
    positionStoreRef.current.clear();
  }, []);

  // WS event listener. submitting 中だけ subscribe / unsubscribe する
  // (= 不要な listener を transport に貼りっぱなしにしない).
  useEffect(() => {
    if (submitState.t !== 'submitting') return;
    const off = transport.onEvent((event) => {
      if (event.t === 'feedback.ack') {
        setSubmitState({ t: 'success' });
        // 1.6 秒で自動 close (= 「送信しました」を読める間隔 + テンポ良く戻れる).
        if (successTimerRef.current !== null) clearTimeout(successTimerRef.current);
        successTimerRef.current = setTimeout(() => {
          successTimerRef.current = null;
          setOpen(false);
          setMessage('');
          setSubmitState({ t: 'idle' });
        }, 1_600);
        return;
      }
      if (event.t === 'feedback.rejected') {
        setSubmitState({ t: 'error', message: mapFeedbackRejectedReason(event.reason) });
        return;
      }
    });
    return off;
  }, [submitState.t, transport]);

  const handleSubmit = useCallback((): void => {
    // 二重押し防止.
    if (submitState.t === 'submitting' || submitState.t === 'success') return;
    const validated = validateFeedbackMessage(message);
    if (validated.outcome !== 'ok') {
      setSubmitState({
        t: 'validating',
        error:
          validated.outcome === 'empty'
            ? 'メッセージを入力してください'
            : `メッセージが長すぎます (${FEEDBACK_MESSAGE_MAX_LENGTH} 文字以内でお願いします)`,
      });
      return;
    }
    // 送信. roomId は room に居る時だけ optional 添付 (= MVP 仕様).
    transport.send(
      currentRoomId !== null
        ? { t: 'feedback.submit', message: validated.trimmed, roomId: currentRoomId }
        : { t: 'feedback.submit', message: validated.trimmed },
    );
    setSubmitState({ t: 'submitting' });
  }, [submitState.t, message, transport, currentRoomId]);

  // 位置スタイル. null → CSS default (右下), 値あり → 絶対座標.
  // dragging 中は cursor を grabbing に. touch-action は drag 動作のため none.
  const positionStyle = useMemo<React.CSSProperties>(() => {
    if (position === null) {
      return { touchAction: 'none' };
    }
    return {
      left: `${position.x}px`,
      top: `${position.y}px`,
      right: 'auto',
      bottom: 'auto',
      touchAction: 'none',
    };
  }, [position]);

  return (
    <>
      {/* フローティングアイコンボタン. 右下 16px 余白、size 36px (= §3.3.1 の
          32-40px レンジ). z-30 で modal (z-[60]) より下、AnnounceBanner 等の
          通常 UI (z-10 程度) より上. メンバーリストの白背景に溶けないよう
          border-2 + shadow-lg + ring で輪郭を強調 (= 2026-05-22 改善).
          ドラッグ移動 + 吹き出し演出は 2026-05-22 前倒し強化 (§3.3.1). */}
      <div className="fixed right-4 bottom-4 z-30" style={positionStyle}>
        {/* 吹き出し. ボタン上 (or 左) にフェードイン/アウトで重ねる.
            aria-hidden で screen reader からは隠す (= 装飾、ボタンの aria-label
            で意味は十分伝わる). pointer-events-none で drag 等を阻害しない. */}
        {bubbleText !== null && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 bottom-full mb-2 whitespace-nowrap rounded-lg border border-[var(--color-accent)] bg-[var(--color-panel)] px-3 py-1.5 text-xs text-[var(--color-text)] shadow-lg ring-1 ring-black/10"
            style={{ animation: 'feedback-bubble-fade 250ms ease-out' }}
          >
            {bubbleText}
            {/* 三角ツノ (= 吹き出しっぽさ). 右下からボタンを指す向き. */}
            <span
              aria-hidden="true"
              className="absolute right-3 bottom-0 block h-2 w-2 translate-y-1/2 rotate-45 border-[var(--color-accent)] border-r border-b bg-[var(--color-panel)]"
            />
          </div>
        )}
        <button
          type="button"
          aria-label="タカミンにメッセージを送る"
          aria-haspopup="dialog"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDoubleClick={handleDoubleClick}
          className={`grid h-[52px] w-[52px] place-items-center rounded-full border-2 border-[var(--color-accent)] bg-white p-0 shadow-lg outline-none ring-1 ring-black/10 transition hover:scale-105 hover:bg-white focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] ${
            dragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{ touchAction: 'none', userSelect: 'none' }}
        >
          <img
            src={dragging ? (waving ? iconFbDrag2 : iconFbDrag1) : waving ? iconFb2 : iconFb1}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="h-12 w-12 object-none"
            style={{
              imageRendering: 'pixelated',
              pointerEvents: 'none',
              // 揺れの振動感が酔いを誘うので rotate は廃止、scale のみで「ぴく」を
              // 表現する. 通常時はやや強め、ドラッグ中は持続切替なので更に控えめに.
              transform: waving ? (dragging ? 'scale(1.05)' : 'scale(1.08)') : 'scale(1)',
              transition: 'transform 180ms ease-out',
            }}
          />
        </button>
      </div>
      {/* Debug: 次の吹き出し発火までのカウントダウン. import.meta.env.DEV 限定。
          prod ビルドでは tree-shaking で消える (= ifブランチ常に false). */}
      {import.meta.env.DEV && debugCountdown !== null && (
        <div className="fixed bottom-2 left-2 z-30 pointer-events-none select-none rounded bg-black/70 px-2 py-1 font-mono text-[10px] text-white">
          next bubble: {debugCountdown}s
        </div>
      )}
      <FeedbackModalView
        open={open}
        message={message}
        onMessageChange={(next) => {
          setMessage(next);
          // 入力途中で validating エラーは消す (= 「再入力中はエラー見えない」UX).
          if (submitState.t === 'validating') setSubmitState({ t: 'idle' });
          // server rejected も再入力で消す (= 修正して再送する流れ).
          if (submitState.t === 'error') setSubmitState({ t: 'idle' });
        }}
        submitState={submitState}
        onSubmit={handleSubmit}
        onCancel={closeModal}
        titleId={titleId}
        messageInputId={messageInputId}
        errorTextId={errorTextId}
        textareaRef={textareaRef}
      />
    </>
  );
};
